import {
  Title,
  Text,
  Stack,
  Paper,
  Button,
  Table,
  Badge,
  Group,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  CopyButton,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useEffect, useState, useCallback } from 'react'
import {
  IconCircleOff,
  IconInfoCircle,
  IconPlus,
  IconTrash,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { normalizeErrorString } from '@medplum/core'
import { Navigation } from './components'
import { CreateOrganizationConnectModal } from './components/CreateOrganizationConnectModal'
import { useClient, type OrpcClient } from '@/hooks/useClient'

type ConnectListItem = Awaited<ReturnType<OrpcClient['listOrganizationConnects']>>['items'][number]

const getReferenceId = (reference?: string): string | undefined => {
  if (!reference) return undefined
  const parts = reference.split('/')
  return parts[parts.length - 1]
}

export function OrganizationConnects() {
  const medplum = useMedplum()
  const client = useClient()
  const profile = medplum.getProfile()

  const [isRoleLoading, setIsRoleLoading] = useState(true)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [items, setItems] = useState<ConnectListItem[]>([])
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    let isActive = true
    const loadRole = async () => {
      setIsRoleLoading(true)
      try {
        if (!profile?.id) {
          if (isActive) {
            setOrganizationId(null)
            setIsRoleLoading(false)
          }
          return
        }
        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (isActive) {
          setOrganizationId(getReferenceId(role?.organization?.reference) ?? null)
        }
      } catch (err) {
        if (!isActive) return
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err) || 'Failed to load practitioner role.',
        })
        setOrganizationId(null)
      } finally {
        if (isActive) setIsRoleLoading(false)
      }
    }
    loadRole()
    return () => {
      isActive = false
    }
  }, [medplum, profile?.id])

  const loadList = useCallback(async () => {
    if (!organizationId) return
    setIsLoadingList(true)
    try {
      const result = await client.listOrganizationConnects({})
      setItems(result.items)
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Failed to load connections',
        message: normalizeErrorString(err) || 'Unable to load organization connections.',
      })
    } finally {
      setIsLoadingList(false)
    }
  }, [client, organizationId])

  useEffect(() => {
    void loadList()
  }, [loadList])

  const handleDelete = (item: ConnectListItem) => {
    modals.openConfirmModal({
      title: 'Remove connection',
      children: (
        <Text size="sm">
          This will permanently remove the connection
          {item.recipient ? ` with ${item.recipient.name}` : ''}. This cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await client.deleteOrganizationConnect({ id: item.id })
          showNotification({ color: 'green', title: 'Connection removed', message: '' })
          await loadList()
        } catch (err) {
          showNotification({
            color: 'red',
            title: 'Failed to remove connection',
            message: normalizeErrorString(err) || 'Unable to remove the connection.',
          })
        }
      },
    })
  }

  return (
    <>
      <Navigation />
      <Stack className="mx-auto w-full max-w-[1000px] p-6" gap="lg">
        <Stack gap={4}>
          <Title order={2}>Organization Connects</Title>
          <Text c="dimmed">
            Manage connections with other organizations. Connected organizations can be used to
            coordinate care across teams.
          </Text>
        </Stack>

        {isRoleLoading ? (
          <Group>
            <Loader size="sm" />
            <Text size="sm">Loading…</Text>
          </Group>
        ) : !organizationId ? (
          <Alert icon={<IconInfoCircle />} color="yellow">
            This feature is available for organization members. Please sign in with an account that
            belongs to an organization.
          </Alert>
        ) : (
          <Paper withBorder p="md">
            <Stack>
              <Group justify="space-between">
                <Text fw={600}>Connections</Text>
                <Button leftSection={<IconPlus size={16} />} onClick={() => setIsModalOpen(true)}>
                  New connect
                </Button>
              </Group>

              {isLoadingList ? (
                <Group>
                  <Loader size="sm" />
                  <Text size="sm">Loading connections…</Text>
                </Group>
              ) : items.length === 0 ? (
                <Text c="dimmed" size="sm">
                  No connections yet. Click "New connect" to invite an organization.
                </Text>
              ) : (
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Counterparty</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {items.map((item) => {
                      const counterparty =
                        item.role === 'initiated'
                          ? item.recipient?.name || item.recipientEmail || 'Pending recipient'
                          : item.initiator.name
                      return (
                        <Table.Tr key={item.id}>
                          <Table.Td>{counterparty}</Table.Td>
                          <Table.Td>
                            <Badge
                              color={
                                item.status === 'accepted'
                                  ? 'green'
                                  : item.status === 'declined'
                                    ? 'gray'
                                    : 'yellow'
                              }
                            >
                              {item.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {item.role === 'initiated' ? 'Initiated' : 'Received'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{new Date(item.createdAt).toLocaleDateString()}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs" justify="flex-end">
                              {item.acceptUrl && (
                                <CopyButton value={item.acceptUrl} timeout={1500}>
                                  {({ copied, copy }) => (
                                    <Tooltip label={copied ? 'Link copied' : 'Copy invite link'}>
                                      <ActionIcon variant="light" onClick={copy}>
                                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              )}
                              <Tooltip label="Remove connection">
                                <ActionIcon
                                  variant="light"
                                  color="red"
                                  onClick={() => handleDelete(item)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>

      <CreateOrganizationConnectModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={() => {
          void loadList()
        }}
      />
    </>
  )
}
